import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import { ModalBody } from "reactstrap";
import styled from "styled-components";
import { mobile } from "../../../design-system/tokens/breakpoints";
import listUtils from "../../../_services/list-utils";
import { clientReducerActions } from "../../../_services/store";
import stringUtils from "../../../_services/string-utils";
import { AuthDataIdentifier } from "../../../_statics/AuthDataType";
import { Modes } from "../../../_statics/CRUDStatics";
import { ErrorMessage, InfoMessage, SuccessMessage } from "../../../_UI/Components/Alerts/PathwayAlertMessages";
import PathwaySingleFileInputGroup from "../../../_UI/Components/Inputs/PathwaySingleFileInputGroup";
import { ModalS } from "../../../_UI/Components/ModalS";
import { LoadingSpinnerButton, PathwayButton } from "../../../_UI/Components/PathwayButtons";
import UncontrolledTabNavigation from "../../../_UI/Components/TabModul/UncontrolledTabNavigation";
import ScreenHeader from "../../../_UI/Components/ScreenHeader";
import { buildClientsSummary } from "./clientsSummary";
import DatevConnectionButton from "../../../_components/DatevConnectionButton";
import ClientAutoUpdateFromProdModal from "../components/ClientAutoUpdateFromProdModal";
import { EnvironmentEnum, getEnvironment } from "../../../EnvironmentUtils";
import { UserRole } from "../../../_statics/UserStatics";
import {
    PathwayFormGrid,
    PathwayFormGroup,
    PathwayInput,
    PathwayLabel,
} from "../../../_UI/StyledComponents/PathwayForm";
import { PathwayTableHeader, PathwayTableHeaderRow } from "../../../_UI/StyledComponents/PathwayTable";
import { amazonpayActions } from "../../../integrations/Amazon/Pay/services/store/amazonpay.actions";
import { reloadUserData } from "../../../user/services/store/authentication.actions";
import APIConnectionContent from "../components/APIConnectionContent";
import ClientInput from "../components/ClientInput";
import ClientPaymentConnectionSetupModal from "../components/ClientPaymentConnectionSetupModal";
import ClientSelectionForConnectedAuthData from "../components/ClientSelectionForConnectedAuthData";
import ClientsScreenClientsTabContent from "../components/ClientsScreenClientsTabContent";
import ConnectedAuthDataInput from "../components/ConnectedAuthDataInput";
import DeleteClientDialog from "../components/DeleteClientDialog";
import DeleteConnectedAuthDataDialog from "../components/DeleteConnectedAuthDataDialog";
import {
    hasClientConnectedAuthData,
    mapClientPropsToDTO,
    mapClientsToConnectedAuthData,
    mapClientsToUniqueDatevSettings,
    mapConnectedAuthDataInputFieldInformation,
    mapDatevSettingsIntoClient,
} from "../services/client.mapper";
import {
    createClientForSelectedOrganization,
    createConnectedAuthDataForClient,
    deleteClient,
    deleteConnectedAuthDataForClient,
    getClientsForSelectedOrganization,
    getRedirectingLinkBySelectedAuthDataType,
    submitEditConnectedAuthData,
    updateClient,
} from "../services/clients.actions";
import { initialClient, initialConnectedAuthData } from "../services/clients.reducer";
import OttoPostInstallHandler from "../components/OttoPostInstallHandler";
import PaypalResponseHandler from "../components/PaypalResponseHandler";

const CLIENTS = "CLIENTS";
const CONNECTIONS = "CONNECTIONS";
const TABS = {
    CLIENTS,
    CONNECTIONS,
};

export const EXTERNAL_AUTH_CONNECTIONS = [
    AuthDataIdentifier.AMAZON,
    AuthDataIdentifier.PAYPAL,
    AuthDataIdentifier.MOLLIE,
    AuthDataIdentifier.STRIPE_CONNECT,
    AuthDataIdentifier.SHOPIFY_APPSTORE_BILLING,
    AuthDataIdentifier.SHOPIFY_APPSTORE_PLATFORM,
    AuthDataIdentifier.TIKTOK_OAUTH,
    AuthDataIdentifier.OTTO_OAUTH,
    AuthDataIdentifier.EBAY_OAUTH,
];

export const CREDENTIAL_BASED_AUTH_CONNECTIONS = [
    AuthDataIdentifier.ABOUT_YOU_API_KEY,
    AuthDataIdentifier.AMAZON_PAY_V2,
    AuthDataIdentifier.KAUFLAND,
    AuthDataIdentifier.WOO_COMMERCE,
    AuthDataIdentifier.CHARGEBEE,
    AuthDataIdentifier.KLARNA,
    AuthDataIdentifier.BRAINTREE,
    AuthDataIdentifier.GOCARDLESS,
    AuthDataIdentifier.SNIPCART,
    AuthDataIdentifier.BILLBEE,
    AuthDataIdentifier.FASTBILL,
    AuthDataIdentifier.UNZER,
    AuthDataIdentifier.XENTRAL,
    AuthDataIdentifier.ZALANDO,
];

const TABLIST = [
    {
        name: "Shops & Accounts",
        identifier: TABS.CLIENTS,
    },
    {
        name: "API Verbindungen",
        identifier: TABS.CONNECTIONS,
    },
];

const ClientsScreen = () => {
    const clientReducer = useSelector((state) => state.client);
    const userRole = useSelector((state) => state.authentication.user.userRole);
    const amazonPay = useSelector((state) => state.integrations.amazonPay);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [currentSelectedSettingIndex, setCurrentSelectedSettingIndex] = useState();

    const [showDatevConnectionModal, setShowDatevConnectionModal] = useState(false);
    const [showAutoUpdateModal, setShowAutoUpdateModal] = useState(false);

    const isSuperUser = userRole === UserRole.SUPER_USER || userRole === UserRole.TECHNICAL_USER;

    const clientsSummary = useMemo(() => buildClientsSummary(clientReducer.clients), [clientReducer.clients]);

    const [selectedClientForAuthData, setSelectedClientForAuthData] = useState("");
    const [skrSettingsUpdated, setSkrSettingsUpdated] = useState(false);
    const [initialSkr, setInitialSkr] = useState(null);
    const [initialAccountNumberLength, setInitialAccountNumberLength] = useState(null);

    const [skrUpdateConfirmed, setSkrUpdateConfirmed] = useState(false);

    const [toggleAmazonPayMigrationModal, setToggleAmazonPayMigrationModal] = useState(true);

    useEffect(() => {
        dispatch(getClientsForSelectedOrganization());
    }, [dispatch]);

    useEffect(() => {
        dispatch(clientReducerActions.setUsedDatevSetting(mapClientsToUniqueDatevSettings(clientReducer.clients)));
        dispatch(clientReducerActions.setUsedConnectedAuthData(mapClientsToConnectedAuthData(clientReducer.clients)));
    }, [clientReducer.clients, dispatch]);

    useEffect(() => {
        if (
            location.hash === "#settings" &&
            !listUtils.isEmpty(clientReducer.clients) &&
            !clientReducer.editClientModal
        ) {
            dispatch(clientReducerActions.setEditClientMode(Modes.EDIT));
            dispatch(clientReducerActions.setEditClient(clientReducer.clients[0]));
            dispatch(clientReducerActions.toggleEditClientModal());
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, location.hash]);

    useEffect(() => {
        if (
            location.hash === "#new-amazon-connection" &&
            !clientReducer.editConnectedAuthDataModalWithoutClient
        ) {
            dispatch(clientReducerActions.resetErrorMessages());
            dispatch(clientReducerActions.setAuthLink(null));
            dispatch(clientReducerActions.setEditConnectedAuthDataMode(Modes.CREATE));
            dispatch(clientReducerActions.setEditClient({ ...initialClient }));
            dispatch(
                clientReducerActions.setEditConnectedAuthData({
                    ...initialConnectedAuthData,
                    type: AuthDataIdentifier.AMAZON,
                })
            );
            dispatch(clientReducerActions.toggleEditConnectedAuthDataModalWithoutClient());
            navigate(`${location.pathname}${location.search}`, { replace: true });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, location.hash]);

    const createNewClient = () => {
        dispatch(clientReducerActions.resetErrorMessages());
        dispatch(clientReducerActions.setEditClientMode(Modes.CREATE));
        if (clientReducer.isGlobalDatevSetting) {
            dispatch(
                clientReducerActions.setEditClient(
                    mapDatevSettingsIntoClient(clientReducer.usedDatevSettings[0], initialClient)
                )
            );
        } else {
            dispatch(clientReducerActions.setEditClient(initialClient));
        }
        dispatch(clientReducerActions.toggleEditClientModal());
    };

    const openEditClientModal = (client) => {
        dispatch(clientReducerActions.resetErrorMessages());
        dispatch(clientReducerActions.setEditClientMode(Modes.EDIT));
        dispatch(clientReducerActions.setEditClient(client));
        dispatch(clientReducerActions.toggleEditClientModal());
        dispatch(clientReducerActions.resetErrorMessages());
        setInitialSkr(client.skr);
        setInitialAccountNumberLength(client.accountNumberLength);
    };

    const cancelEditClient = useCallback(() => {
        dispatch(clientReducerActions.setEditClientMode(null));
        dispatch(clientReducerActions.setEditClient(initialClient));
        dispatch(clientReducerActions.toggleEditClientModal());
        setSkrSettingsUpdated(false);
        setInitialSkr(null);
        setInitialAccountNumberLength(null);
    }, [dispatch, setSkrSettingsUpdated, setInitialSkr, setInitialAccountNumberLength]);

    const openDeleteClientModal = (client) => {
        dispatch(clientReducerActions.resetErrorMessages());
        dispatch(clientReducerActions.setEditClient(client));
        dispatch(clientReducerActions.toggleDeleteClientModal());
    };

    const cancelDeleteClient = () => {
        dispatch(clientReducerActions.setEditClient(initialClient));
        dispatch(clientReducerActions.toggleDeleteClientModal());
    };

    const openDeleteConnectedAuthDataModal = (connectedAuthData) => {
        dispatch(clientReducerActions.resetErrorMessages());
        dispatch(clientReducerActions.setEditConnectedAuthData(connectedAuthData));
        dispatch(clientReducerActions.toggleDeleteConnectedAuthDataModal());
    };

    const cancelDeleteConnectedAuthDataModal = () => {
        dispatch(clientReducerActions.setEditConnectedAuthData(initialConnectedAuthData));
        dispatch(clientReducerActions.toggleDeleteConnectedAuthDataModal());
    };

    const submitEditClient = () => {
        if (clientReducer.editClientMode === Modes.CREATE) {
            dispatch(createClientForSelectedOrganization(mapClientPropsToDTO(clientReducer.editClient)));
        } else {
            dispatch(updateClient(mapClientPropsToDTO(clientReducer.editClient)));
        }
        setSkrSettingsUpdated(false);
        setSkrUpdateConfirmed(false);
        setInitialSkr(null);
        setInitialAccountNumberLength(null);
    };

    const handleEditClientChange = (e) => {
        if (!e.target.validity.valid) {
            return;
        }
        let value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const { name } = e.target;
        if (name === "businessYearMonth") {
            dispatch(
                clientReducerActions.setEditClient({
                    ...clientReducer.editClient,
                    [name]: value,
                    businessYearDay: 1,
                })
            );
        } else {
            dispatch(
                clientReducerActions.setEditClient({
                    ...clientReducer.editClient,
                    [name]: value,
                })
            );
        }
    };

    const handleDatevDocumentTypeChange = (e) => {
        dispatch(
            clientReducerActions.setEditClient({
                ...clientReducer.editClient,
                datevDocumentType: stringUtils.isBlank(e.target.value) ? null : e.target.value,
            })
        );
    };

    const handleSkrSettingsChange = (e) => {
        if (!e.target.validity.valid) {
            return;
        }
        let value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const { name } = e.target;

        let isValueChanged;
        if (name === "accountNumberLength") {
            isValueChanged = value.toString() !== initialAccountNumberLength && value.toString() !== "";
        } else {
            isValueChanged = value.toString() !== initialSkr && value.toString() !== "";
        }

        setSkrSettingsUpdated(isValueChanged);
        setSkrUpdateConfirmed(!isValueChanged);
        const newValue = stringUtils.isBlank(value) ? null : value;

        dispatch(
            clientReducerActions.setEditClient({
                ...clientReducer.editClient,
                [name]: newValue,
                skrSettingsUpdated: isValueChanged,
            })
        );
    };

    const confirmSkrUpdateForExistingIntegrations = (e) => {
        e.preventDefault();
        setSkrUpdateConfirmed(true);
        dispatch(
            clientReducerActions.setEditClient({
                ...clientReducer.editClient,
                updateSkrForExistingIntegrations: true,
            })
        );
    };

    const confirmSkrUpdate = (e) => {
        e.preventDefault();
        setSkrUpdateConfirmed(true);
    };

    const redirectToDatevSettings = useCallback(() => {
        cancelEditClient();
        setShowDatevConnectionModal(true);
    }, [cancelEditClient, setShowDatevConnectionModal]);

    const createNewConnectedAuthDataForClient = (client) => {
        dispatch(clientReducerActions.resetErrorMessages());
        dispatch(clientReducerActions.setEditConnectedAuthDataMode(Modes.CREATE));
        const preparedConnectedAuthData = { ...initialConnectedAuthData };
        preparedConnectedAuthData.clientUUID = client.externalUUID;
        dispatch(clientReducerActions.setEditConnectedAuthData(preparedConnectedAuthData));
        dispatch(clientReducerActions.toggleEditConnectedAuthDataModalForClient());
    };

    const createNewConnectedAuthDataWithoutClient = () => {
        dispatch(clientReducerActions.resetErrorMessages());
        dispatch(clientReducerActions.setEditConnectedAuthDataMode(Modes.CREATE));
        dispatch(clientReducerActions.setEditClient({ ...initialClient }));
        dispatch(clientReducerActions.setEditConnectedAuthData({ ...initialConnectedAuthData }));
        dispatch(clientReducerActions.toggleEditConnectedAuthDataModalWithoutClient());
    };

    const editExistingConnectedAuthData = (connectedAuthData) => {
        dispatch(clientReducerActions.resetErrorMessages());
        dispatch(clientReducerActions.setEditConnectedAuthData(connectedAuthData));
        dispatch(clientReducerActions.setEditConnectedAuthDataMode(Modes.EDIT));
        dispatch(
            clientReducerActions.setInputLableInformation(
                mapConnectedAuthDataInputFieldInformation(connectedAuthData, userRole)
            )
        );
        dispatch(clientReducerActions.toggleEditConnectedAuthDataModalForClient());
    };

    const cancelCreateNewConnectedAuthDataWithoutClient = () => {
        dispatch(clientReducerActions.setEditConnectedAuthDataMode(null));
        dispatch(clientReducerActions.setEditConnectedAuthData(initialConnectedAuthData));
        dispatch(clientReducerActions.toggleEditConnectedAuthDataModalWithoutClient());
        dispatch(clientReducerActions.setAuthLink(null));
    };

    const clearWhitespaces = (e) => {
        const { name, value } = e.target;
        if (value) {
            dispatch(
                clientReducerActions.setEditClient({
                    ...clientReducer.editClient,
                    [name]: value.split(" ").join(""),
                })
            );
        }
    };

    const chooseExistingDatevSetting = (e) => {
        const selectedDatevSetting = clientReducer.usedDatevSettings[e.target.value];
        setCurrentSelectedSettingIndex(e.target.value);
        const updatedClient = mapDatevSettingsIntoClient(selectedDatevSetting, clientReducer.editClient);
        dispatch(clientReducerActions.setEditClient(updatedClient));
    };

    const chooseClientForConnectedAuth = (e) => {
        const { value } = e.target;
        if (value === "NEW_CLIENT") {
            setSelectedClientForAuthData(value);
            dispatch(clientReducerActions.setAuthLink(null));
            dispatch(clientReducerActions.setEditConnectedAuthData({ ...initialConnectedAuthData }));
            dispatch(clientReducerActions.setEditClient({ ...initialClient }));
        } else if (!stringUtils.isBlank(value)) {
            setSelectedClientForAuthData(value);
            const selectedType = clientReducer.editConnectedAuthData.type;
            const preparedConnectedAutHData = { ...initialConnectedAuthData, type: selectedType, clientUUID: value };
            dispatch(clientReducerActions.setEditConnectedAuthData(preparedConnectedAutHData));
            if (EXTERNAL_AUTH_CONNECTIONS.includes(selectedType)) {
                dispatch(clientReducerActions.setAuthLink(null));
                const authLink = dispatch(getRedirectingLinkBySelectedAuthDataType(selectedType, value, true));
                if (authLink) {
                    dispatch(clientReducerActions.setAuthLink(authLink));
                }
            }
        }
    };

    const handleConnectedAuthDataChange = (e) => {
        const { name, value } = e.target;
        dispatch(
            clientReducerActions.setEditConnectedAuthData({
                ...clientReducer.editConnectedAuthData,
                [name]: value,
            })
        );
    };

    const handleDirectAuthDataCreation = async () => {
        const authData = {
            ...clientReducer.editConnectedAuthData,
            clientUUID: amazonPay.clientToMigrate.externalUUID,
            type: "AMAZON_PAY_V2",
        };
        await dispatch(createConnectedAuthDataForClient(authData)).then((res) => {
            dispatch(amazonpayActions.getAllAmazonPayConfigurations);
        });
    };

    const handleAmazonPayMigration = () => {
        // If AuthData created, migrate the configuration

        dispatch(amazonpayActions.migrateToVersion2(amazonPay.configurationToMigrate)).then((res) => reloadUserData());
    };

    // Amazon Pay private key upload
    const handleAmazonPayFileUpload = (e) => {
        dispatch(clientReducerActions.resetErrorMessages());
        const file = e.target.files[0];
        if (file) {
            const extention = file.name.substring(file.name.length - 4);
            if (extention !== ".pem") {
                dispatch(clientReducerActions.setEditAuthDataErrorMessage("Nur .pem Dateien verwendbar."));
            } else {
                dispatch(clientReducerActions.setUploadedDataFile(file));
                // Read AmazonPay private key and set on editConnectedAuthData
                let reader = new FileReader();
                reader.readAsText(e.target.files[0], "UTF-8");
                reader.onload = function () {
                    let key = reader.result;
                    dispatch(
                        clientReducerActions.setEditConnectedAuthData({
                            ...clientReducer.editConnectedAuthData,
                            value: key,
                        })
                    );
                };
                reader.onerror = function () {
                    console.error(reader.error);
                };
            }
        } else {
            console.error("File input is undefined or file is not selected");
        }
    };

    // Amazon Pay private key file removal
    const removeFile = (fileToBeRemoved) => {
        if (clientReducer.dataFileToImport && clientReducer.dataFileToImport.name === fileToBeRemoved.name) {
            dispatch(clientReducerActions.setUploadedDataFile(null));
        }
    };

    const clearMigrationModal = () => {
        setToggleAmazonPayMigrationModal(!toggleAmazonPayMigrationModal);
        dispatch(clientReducerActions.resetAuthDataMessages());
        dispatch(amazonpayActions.resetState());
        dispatch(reloadUserData());
        removeFile(clientReducer.dataFileToImport);
    };

    const redirectToAmazonPay = () => {
        dispatch(reloadUserData());
        navigate("/integrations/amazon/pay/v2");
    };

    const hasAuthData = () => {
        return (
            !amazonPay.migrationErrorMessage &&
            (hasClientConnectedAuthData(amazonPay.clientToMigrate, "AMAZON_PAY_V2") ||
                clientReducer.editAuthDataSuccessMessage)
        );
    };

    return (
        <>
            <PaypalResponseHandler />
            <OttoPostInstallHandler />

            <ModalS
                isOpen={clientReducer.editClientModal}
                isLoading={clientReducer.isEditClientLoading}
                disabled={
                    clientReducer.isEditClientLoading ||
                    clientReducer.isLoading ||
                    (skrSettingsUpdated && !skrUpdateConfirmed)
                }
                eyebrow="Shops & Accounts"
                title={"Account - Einstellungen"}
                primaryButtonText={"Speichern"}
                toggleModal={cancelEditClient}
                handleSubmit={submitEditClient}
                errorMessage={clientReducer.editClientErrorMessage}
                size={"lg"}
                submitAndToggle={false}
                buttonCyData="save-client-button"
                dataCy="edit-client"
            >
                <ClientInput
                    chooseExistingDatevSetting={chooseExistingDatevSetting}
                    currentSelectedSettingIndex={currentSelectedSettingIndex}
                    handleEditClientChange={handleEditClientChange}
                    handleDatevDocumentTypeChange={handleDatevDocumentTypeChange}
                    clearWhitespaces={clearWhitespaces}
                    handleSkrSettingsChange={handleSkrSettingsChange}
                    skrSettingsUpdated={skrSettingsUpdated}
                    confirmSkrUpdateForExistingIntegrations={confirmSkrUpdateForExistingIntegrations}
                    confirmSkrUpdate={confirmSkrUpdate}
                    skrUpdateConfirmed={skrUpdateConfirmed}
                    redirectToDatevSettings={redirectToDatevSettings}
                />
            </ModalS>

            <ModalS
                disabled={clientReducer.isDeleteClientLoading}
                handleSubmit={() => dispatch(deleteClient(clientReducer.editClient))}
                isLoading={clientReducer.isLoading || clientReducer.isDeleteClientLoading}
                isOpen={clientReducer.deleteClientModal}
                primaryButtonText={"Löschen"}
                title={"Account/Shop löschen"}
                toggleModal={cancelDeleteClient}
                submitAndToggle={false}
                buttonCyData="delete-client-button"
            >
                <DeleteClientDialog />
            </ModalS>

            <ModalS
                disabled={clientReducer.isDeleteAuthDataLoading}
                handleSubmit={() => dispatch(deleteConnectedAuthDataForClient(clientReducer.editConnectedAuthData))}
                isLoading={clientReducer.isLoading || clientReducer.isDeleteAuthDataLoading}
                isOpen={clientReducer.deleteConnectedAuthDataModal}
                primaryButtonText={"Löschen"}
                title={"Account/Shop löschen"}
                toggleModal={cancelDeleteConnectedAuthDataModal}
                submitAndToggle={false}
                buttonCyData="delete-client-auth-data-button"
            >
                <DeleteConnectedAuthDataDialog />
            </ModalS>

            <ClientPaymentConnectionSetupModal
                clearWhitespaces={clearWhitespaces}
                handleConnectedAuthDataChange={handleConnectedAuthDataChange}
                handleFileUpload={handleAmazonPayFileUpload}
            />

            <ModalS
                isOpen={clientReducer.editConnectedAuthDataModalWithoutClient}
                isLoading={clientReducer.isEditAuthDataLoading || clientReducer.prepareAuthDataOnboardingLoading}
                disabled={
                    clientReducer.isEditAuthDataLoading ||
                    clientReducer.isLoading ||
                    !clientReducer.editConnectedAuthData.type ||
                    (clientReducer.editConnectedAuthDataMode === Modes.CREATE &&
                        EXTERNAL_AUTH_CONNECTIONS.includes(clientReducer.editConnectedAuthData.type) &&
                        !clientReducer.authLink)
                }
                eyebrow={clientReducer.selectedClient ? clientReducer.selectedClient.name : "Shops & Accounts"}
                title={"Verbindung erstellen"}
                primaryButtonText={
                    clientReducer.editConnectedAuthDataMode === Modes.CREATE &&
                    EXTERNAL_AUTH_CONNECTIONS.includes(clientReducer.editConnectedAuthData.type)
                        ? "Verbindung einrichten"
                        : "Speichern"
                }
                errorMessage={clientReducer.editAuthDataErrorMessage}
                size={"lg"}
                submitAndToggle={false}
                handleSubmit={
                    clientReducer.editConnectedAuthDataMode === Modes.CREATE &&
                    EXTERNAL_AUTH_CONNECTIONS.includes(clientReducer.editConnectedAuthData.type)
                        ? () => (window.location.href = clientReducer.authLink)
                        : () => dispatch(submitEditConnectedAuthData(clientReducer))
                }
                toggleModal={cancelCreateNewConnectedAuthDataWithoutClient}
            >
                <ClientScreenInputGrid>
                    <PathwayFormGrid>
                        <ClientSelectionForConnectedAuthData
                            selectedClientForAuthData={selectedClientForAuthData}
                            chooseExistingClient={chooseClientForConnectedAuth}
                            handleEditClientChange={handleEditClientChange}
                        />
                    </PathwayFormGrid>
                    <PathwayFormGrid>
                        <PathwayTableHeaderRow>
                            <PathwayTableHeader>Verbindung - Einstellungen</PathwayTableHeader>
                        </PathwayTableHeaderRow>
                        <ConnectedAuthDataInput
                            handleConnectedAuthDataChange={handleConnectedAuthDataChange}
                            selectedClientForAuthData={selectedClientForAuthData}
                            handleFileUpload={handleAmazonPayFileUpload}
                            handleAmazonPayMigration={handleAmazonPayMigration}
                        />
                    </PathwayFormGrid>
                </ClientScreenInputGrid>
            </ModalS>

            {/* Special case - Amazon Pay migration from V1 to V2 (via redirect) - also present in ConnectedAuthDataInput */}
            {amazonPay.configurationToMigrate && amazonPay.clientToMigrate && (
                <ModalS
                    isOpen={toggleAmazonPayMigrationModal}
                    isLoading={amazonPay.isUpdateLoading}
                    hidePrimaryButton={true}
                    // disabled={blockInputFields}
                    title={amazonPay.configurationToMigrate.clientName}
                    primaryButtonText={"Update"}
                    toggleModal={clearMigrationModal}
                    handleSubmit={handleAmazonPayMigration}
                    errorMessage={amazonPay.updateErrorMessage}
                    size={"lg"}
                    submitAndToggle={false}
                >
                    <ModalBody>
                        {!hasClientConnectedAuthData(amazonPay.clientToMigrate, "AMAZON_PAY_V2") && (
                            <>
                                <PathwayFormGroup>
                                    <PathwayLabel>Public key</PathwayLabel>
                                    <PathwayInput
                                        type="text"
                                        name="key"
                                        data-cy="key"
                                        placeholder={"..."}
                                        value={clientReducer.editAccessData.key}
                                        onChange={handleConnectedAuthDataChange}
                                        onBlur={clearWhitespaces}
                                    />
                                </PathwayFormGroup>
                                <PathwaySingleFileInputGroup
                                    name={"privateKeyFileUpload"}
                                    handleChange={handleAmazonPayFileUpload}
                                    value={clientReducer?.dataFileToImport}
                                    label={"Amazon .pem Datei"}
                                    dataCy={"amazon-pay-private-key-file-input"}
                                />
                            </>
                        )}
                        {!amazonPay.migrationSuccessMessage && (
                            <>
                                {hasAuthData() && (
                                    <InfoMessage useTopMargin={true}>
                                        Eine Verbindung zu Amazon Pay wurde gefunden. Das Update wird automatisch nach
                                        dem Klick auf "Integration aktualisieren" durchgeführt.
                                    </InfoMessage>
                                )}
                                {!hasAuthData() && (
                                    <InfoMessage useTopMargin={true}>
                                        Hinweis: In diesem{" "}
                                        <a
                                            href="https://developer.amazon.com/de/docs/amazon-pay-checkout/get-set-up-for-integration.html#5-get-your-public-key-id"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {" "}
                                            Wissensartikel
                                        </a>{" "}
                                        findest du eine Anleitung wie du den Public Key bei Amazon erhältst. Bitte
                                        beachte, dass du den Public Key für <b>Amazon Pay (Production View)</b>{" "}
                                        benötigst. Wenn du den Public Key und die .pem Datei hochgeladen hast, updaten
                                        wir automatisch deine Amazon Pay Integration.{" "}
                                    </InfoMessage>
                                )}
                                <SuccessMessage
                                    messages={clientReducer.editAuthDataSuccessMessage}
                                    useTopMargin={true}
                                />
                                <ErrorMessage messages={clientReducer.editAuthDataErrorMessage} />
                                <ErrorMessage messages={amazonPay.migrationErrorMessage} />

                                {!clientReducer.editAuthDataSuccessMessage && !amazonPay.hasAuthData && (
                                    <LoadingSpinnerButton
                                        loading={clientReducer.isEditAuthDataLoading}
                                        onClick={handleDirectAuthDataCreation}
                                    >
                                        Zugangsdaten aktualisieren
                                    </LoadingSpinnerButton>
                                )}

                                {hasAuthData() && (
                                    <LoadingSpinnerButton
                                        loading={amazonPay.subloading}
                                        onClick={handleAmazonPayMigration}
                                    >
                                        Integration aktualisieren
                                    </LoadingSpinnerButton>
                                )}

                                {}
                            </>
                        )}
                        {amazonPay.migrationSuccessMessage && (
                            <>
                                <SuccessMessage messages={amazonPay.migrationSuccessMessage} useTopMargin={true} />
                                <PathwayButton onClick={() => redirectToAmazonPay()}>Zu Amazon Pay gehen</PathwayButton>
                            </>
                        )}
                    </ModalBody>
                </ModalS>
            )}

            {clientReducer && (
                <>
                    <ScreenHeader
                        labelDataCy="clients-screen-header"
                        eyebrow="Shops & Accounts"
                        header={clientsSummary}
                    >
                        {getEnvironment() === EnvironmentEnum.DEV && isSuperUser && (
                            <PathwayButton
                                color="warning"
                                onClick={() => setShowAutoUpdateModal(true)}
                                dataCy="prod-sync-button"
                            >
                                Prod Sync
                            </PathwayButton>
                        )}
                        <DatevConnectionButton
                            clients={clientReducer.clients}
                            isOpen={showDatevConnectionModal}
                            onClose={() => setShowDatevConnectionModal(false)}
                            dataCy="datev-connection-modal-button"
                        />
                        <PathwayButton onClick={createNewClient} dataCy="client-add-button" name="create">
                            Account hinzufügen
                        </PathwayButton>
                    </ScreenHeader>

                    <ClientAutoUpdateFromProdModal
                        showModal={getEnvironment() === EnvironmentEnum.DEV && showAutoUpdateModal}
                        closeModal={() => setShowAutoUpdateModal(false)}
                    />

                    <UncontrolledTabNavigation
                        tabList={TABLIST}
                        dataCy={"client-screen-navigation"}
                        defaultTab={
                            new URLSearchParams(location.search).get("tab") === "connections"
                                ? TABS.CONNECTIONS
                                : TABS.CLIENTS
                        }
                    >
                        <ClientsScreenClientsTabContent
                            createNewClient={createNewClient}
                            createNewConnectedAuthDataForClient={createNewConnectedAuthDataForClient}
                            openEditClientModal={openEditClientModal}
                            editExistingConnectedAuthData={editExistingConnectedAuthData}
                            openDeleteClientModal={openDeleteClientModal}
                            showDatevConnectionModal={showDatevConnectionModal}
                            setShowDatevConnectionModal={setShowDatevConnectionModal}
                        />

                        <APIConnectionContent
                            createNewConnectedAuthDataWithoutClient={createNewConnectedAuthDataWithoutClient}
                            openEditConnectedAuthDataModal={editExistingConnectedAuthData}
                            openDeleteConnectedAuthDataModal={openDeleteConnectedAuthDataModal}
                        />
                    </UncontrolledTabNavigation>
                </>
            )}
        </>
    );
};

export default ClientsScreen;

export const TwoColumnsRow = styled.div`
    display: grid;
    grid-template-columns: 0.5fr 0.5fr;
    column-gap: var(--gap-l);

    ${mobile} {
        grid-template-columns: 1fr;
        row-gap: var(--gap-m);
    }
`;
export const ThreeColumnsRow = styled.div`
    display: grid;
    grid-template-columns: 0.33fr 0.33fr 0.33fr;
    column-gap: var(--gap-l);
`;

export const ClientScreenInputGrid = styled.div`
    display: grid;
    row-gap: var(--gap-xl);
`;
